/**
 *
 * Dynamicly loaded polyfills.
 *
 * Here we do feature detection for functionality which we assume should be available in > 90% of currently used browsers.
 * As a result we can omit loading the legacy browser polyfills for those estimated 90% of users.
 * For legacy browsers we first load the "large" legacy-polyfill-bundle and delay the app bootstrapping.
 *
 * See also https://philipwalton.com/articles/loading-polyfills-only-when-needed/
 */

/**
 * Feature detecting commonly available browser functionality,
 * loading legacyBrowserPolyfills if needed and calling the initialize argument in the end.
 *
 * @param {function} initialize the app bootstrapping function to call immediately if all feature detection passes or after loading the polyfill.
 * @returns {void} nothing
 *
 */
export const loadLegacyBrowserPolyfillsIfNeededAndInitialize = initialize => {
  // Safari, in Private Browsing Mode, looks like it supports localStorage but all calls to setItem
  // throw QuotaExceededError. We're going to detect this and just silently drop any calls to setItem
  // to avoid the entire page breaking, without having to do a check at each usage of Storage.
  // Source: https://gist.github.com/philfreo/68ea3cd980d72383c951

  if (typeof window.localStorage === 'object') {
    try {
      window.localStorage.setItem('localStorage', '1')
      window.localStorage.removeItem('localStorage')
    } catch (e) {
      Storage.prototype._setItem = Storage.prototype.setItem
      Storage.prototype.setItem = function() {}
    }
  }

  // feature detection

  if (
    !Array.from ||
    !Array.prototype.find ||
    !Array.prototype.findIndex ||
    !Array.prototype.includes ||
    !Array.prototype.forEach ||
    !Array.prototype.flat ||
    !Array.prototype.flatMap ||
    !NodeList.prototype.forEach ||
    !Element.prototype.closest ||
    !Element.prototype.matches ||
    !Object.is ||
    !Object.entries ||
    !Object.values ||
    !Object.getOwnPropertySymbols ||
    !String.prototype.includes ||
    !String.prototype.normalize ||
    !String.prototype.padEnd ||
    !String.prototype.padStart ||
    !String.prototype.startsWith ||
    !window.HTMLPictureElement ||
    !window.localStorage ||
    !window.sessionStorage
  )
    import(
      /* webpackChunkName: 'legacyBrowserPolyfills' */ './legacyBrowserPolyfills'
    ).then(initialize)
  else initialize()
}
